import { Autocomplete, Grid, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { FormikProps } from 'formik';
import { dictionaryService, DictionaryItem } from '../services/dictionaryService';
import { useEffect, useState } from 'react';
import { FormValues } from '../FormPagePerson';
import { useLanguage } from '../i18n/LanguageContext';

// Wylicza datę urodzenia z numeru PESEL (pierwsze 6 cyfr: RRMMDD, miesiąc koduje stulecie).
const peselToBirthDate = (pesel: string): Date | null => {
    if (!/^\d{11}$/.test(pesel)) return null;
    const year = parseInt(pesel.substring(0, 2), 10);
    let month = parseInt(pesel.substring(2, 4), 10);
    const day = parseInt(pesel.substring(4, 6), 10);
    let century: number;
    if (month >= 1 && month <= 12) century = 1900;
    else if (month >= 21 && month <= 32) { century = 2000; month -= 20; }
    else if (month >= 41 && month <= 52) { century = 2100; month -= 40; }
    else if (month >= 61 && month <= 72) { century = 2200; month -= 60; }
    else if (month >= 81 && month <= 92) { century = 1800; month -= 80; }
    else return null;
    const fullYear = century + year;
    const date = new Date(fullYear, month - 1, day);
    // odrzuć niepoprawne daty (np. dzień 32, luty 30)
    if (date.getFullYear() !== fullYear || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
    return date;
};

const BillingDetails = ({ formik }: { formik: FormikProps<FormValues> }) => {
    const { values, handleChange, handleBlur, errors, touched, setFieldValue } = formik;
    const { t } = useLanguage();
    const [citizenshipOptions, setCitizenshipOptions] = useState<DictionaryItem[]>([]);
    const [taxOfficeOptions, setTaxOfficeOptions] = useState<DictionaryItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [citizenships, taxOffices] = await Promise.all([
                    dictionaryService.getCitizenships(),
                    dictionaryService.getTaxOffices(),
                ]);
                setCitizenshipOptions(citizenships);
                setTaxOfficeOptions(taxOffices);
            } catch (error) {
                // Dictionary loading error handled silently
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    return (
        <Grid container spacing={6} alignItems="flex-start" direction={{ xs: 'column', sm: 'row' }} sx={{ marginTop: '60px' }}>
            <Grid size={{ xs: 12, sm: 12, md: 5 }} sx={{ height: 'auto' }}>
                <Typography variant="h6" sx={{ margin: 0, padding: '0px' }}>{t('section.billingDetails')}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 7 }} >
                <Stack>
                    <Grid container spacing={4} direction={{ xs: 'column', sm: 'row' }}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label={t('field.pesel')}
                                name="pesel"
                                value={values.pesel}
                                onChange={(e) => {
                                    handleChange(e);
                                    const birth = peselToBirthDate(e.target.value);
                                    if (birth) setFieldValue('dateOfBirth', birth);
                                }}
                                onBlur={handleBlur}
                                error={touched.pesel && Boolean(errors.pesel)}
                                helperText={touched.pesel && errors.pesel}
                                type="tel"
                                slotProps={{
                                    input: {
                                        inputMode: 'numeric',
                                    },
                                }}
                                inputProps={{
                                    maxLength:11,
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <DatePicker
                                label={t('field.dateOfBirth')}
                                value={
                                    values.dateOfBirth
                                        ? dayjs.isDayjs(values.dateOfBirth)
                                            ? values.dateOfBirth
                                            : dayjs(values.dateOfBirth)
                                        : null
                                }
                                maxDate={dayjs(new Date())}
                                onChange={(value) => {
                                    // value to Dayjs | null – konwertujemy do JS Date
                                    formik.setFieldValue('dateOfBirth', value ? value.toDate() : null);
                                }}
                                format="DD.MM.YYYY"
                                slotProps={{
                                    textField: {
                                        name: 'dateOfBirth',
                                        onBlur: formik.handleBlur,
                                        error: formik.touched.dateOfBirth && Boolean(formik.errors.dateOfBirth),
                                        helperText: formik.touched.dateOfBirth && formik.errors.dateOfBirth,
                                        fullWidth: true,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            {loading ? (
                                <Skeleton variant="rectangular" height={56} />
                            ) : (
                                <Autocomplete
                                    disablePortal
                                    options={citizenshipOptions}
                                    getOptionLabel={(option) => option.label}
                                    value={citizenshipOptions.find(opt => values.citizenship.includes(opt.label)) || null}
                                    onChange={(_, newValue) =>
                                        setFieldValue('citizenship', newValue ? [newValue.label] : [])
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('field.citizenship')}
                                            name="citizenship"
                                            onBlur={handleBlur}
                                            error={touched.citizenship && Boolean(errors.citizenship)}
                                            helperText={touched.citizenship && errors.citizenship}
                                        />
                                    )}
                                    sx={{ width: '100%' }}
                                />)}
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            {loading ? (
                                <Skeleton variant="rectangular" height={56} />
                            ) : (<Autocomplete
                                disablePortal
                                options={taxOfficeOptions}
                                getOptionLabel={(option) => option.label}
                                value={taxOfficeOptions.find(opt => opt.value=== values.taxOffice) || null}
                                // onChange={(_, newValue) => setFieldValue('taxOffice', newValue?.value || '')}

                                onChange={(_, newValue) => {
                                    if (newValue) {
                                      setFieldValue('taxOffice', newValue.value);      // pole stringowe — walidacja
                                      setFieldValue('taxOfficeDict', {'value': newValue?.value, 'label': newValue?.label});        // pole obiektowe — do API
                                    } else {
                                      setFieldValue('taxOffice', '');
                                      setFieldValue('taxOfficeDict', { value: '', label: '' });
                                    }
                                  }
                                }

                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('field.taxOffice')}
                                        name="taxOffice"
                                        onBlur={handleBlur}
                                        error={touched.taxOffice && Boolean(errors.taxOffice)}
                                        helperText={touched.taxOffice && errors.taxOffice}
                                    />
                                )}
                                sx={{ width: '100%' }}
                            />)}

                        </Grid>
                    </Grid>
                </Stack>
            </Grid>
        </Grid>
    );
};

export default BillingDetails;
